/**
 * Schema Generators
 * ==================
 * Pluggable generators for structured data (JSON-LD).
 * Same pattern as parsers.js: each schema type has its own generator,
 * selected via ENTITY.schemaType.
 *
 * To add a new schema type:
 *   1. Create a generateXxxSchema(post, entity, site, assets, baseUrl) function
 *   2. Register it in SCHEMA_GENERATORS
 *   3. Set schemaType in the entity config
 */

import emojiRegex from 'emoji-regex';

const emojiRegexPattern = emojiRegex();

// ─── Shared Helpers ────────────────────────────────────

export const removeEmojis = (str) => (str || '').replace(emojiRegexPattern, '').trim();

export const extractDescription = (description) => {
  const lines = (description || '').split('\n').filter(line => line.trim().length > 0);
  if (lines.length === 0) return '';
  return lines[1] || lines[0] || '';
};

// ─── Private: post time parsing ────────────────────────

const parsePostTime = (description) => {
  if (!description) return null;
  const text = description.slice(0, 600);

  // ⏳ emoji prefix
  let m = text.match(/(?:⏰|⏳|⌛|🕐|🕑|🕒|🕓|🕔|🕕)\s*(\d+)\s*(?:min|minutos?|mins?)\b/i);
  if (m) return parseInt(m[1]);

  // bare number + min
  m = text.match(/(\d+)\s*(?:min|minutos?|mins?)\b/i);
  if (m) return parseInt(m[1]);

  // "1h 30min" or "2 horas 15 minutos"
  m = text.match(/^(\d+)\s*h(?:oras?)?\s*(\d+)\s*(?:min|minutos?)?/im);
  if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);

  return null;
};

// ─── Private: post step extraction ─────────────────────

const parsePostSteps = (description, slug, imageUrl, baseUrl) => {
  const stepLines = (description || '').split('\n');
  const steps = [];
  let inSteps = false;

  for (const line of stepLines) {
    const clean = line.replace(/[^\w\s]/g, '').toLowerCase().trim();
    const isStepHeader = ['pasos', 'instrucciones', 'preparación', 'preparacion', 'how to']
      .some(kw => clean.includes(kw));

    if (isStepHeader && !inSteps) {
      inSteps = true;
      continue;
    }

    if (inSteps) {
      if (line.length < 3 || ['👣', '🔪', '📝', '🍽️', '⏰'].some(e => line.includes(e))) break;
      if (/^#{1,3}\s/.test(line)) break;

      const stepText = line.replace(/^[-•*]\s*/, '').replace(/^\d+[.)]\s*/, '').trim();
      if (stepText && stepText.length > 5 && !stepText.startsWith('http')) {
        steps.push({
          '@type': 'HowToStep',
          name: `Paso ${steps.length + 1}`,
          text: stepText,
          url: `${baseUrl}/${slug}/`,
          image: `${baseUrl}/${imageUrl}`,
        });
      }
    }
  }

  return steps;
};

// ─── Recipe Generator ──────────────────────────────────

const generateRecipeSchema = (post, entity, site, assets, baseUrl) => {
  const timeMinutes = parsePostTime(post.description);
  const postSteps = parsePostSteps(post.description, post.slug, post.imageUrl, baseUrl);
  const itemsField = entity.dataFields.items;

  const schema = {
    '@type': 'Recipe',
    name: removeEmojis(post.name),
    description: extractDescription(post.description) || '',
    image: `${baseUrl}/${post.imageUrl}`,
    url: `${baseUrl}/${entity.routes.detailPath}/${post.slug}/`,
    recipeCategory: post.tags?.length ? post.tags[0] : entity.label,
    recipeCuisine: entity.defaults.cuisine,
    ...(timeMinutes !== null ? { totalTime: `PT${timeMinutes}M` } : {}),
    recipeYield: entity.defaults.servings,
    recipeIngredient: post[itemsField] && post[itemsField].length > 0 ? post[itemsField] : [],
    recipeInstructions: postSteps.length > 0 ? postSteps : [
      {
        '@type': 'HowToStep',
        name: 'Paso 1',
        text: post.description.split('\n\n')[0] || post.description,
        url: `${baseUrl}/${entity.routes.detailPath}/${post.slug}/`,
        image: `${baseUrl}/${post.imageUrl}`,
      },
    ],
    keywords: post.tags ? post.tags.join(', ') : '',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: entity.defaults.rating?.value || 4.5,
      reviewCount: entity.defaults.rating?.reviewCount || 0,
      ratingCount: entity.defaults.rating?.ratingCount || 0,
    },
    datePublished: post.date || new Date().toISOString(),
    isAccessibleForFree: true,
  };

  if (post.instagramUrl) {
    const embedUrl = post.instagramUrl.endsWith('/')
      ? `${post.instagramUrl}embed/`
      : `${post.instagramUrl}/embed/`;
    schema.video = {
      '@type': 'VideoObject',
      name: removeEmojis(post.name),
      description: post.description.split('\n').filter(l => l.trim().length > 0)[1] || '',
      contentUrl: post.instagramUrl,
      embedUrl,
      uploadDate: post.date || new Date().toISOString(),
      thumbnailUrl: `${baseUrl}/${post.imageUrl}`,
      url: post.instagramUrl,
    };
  }

  return schema;
};

// ─── Article Generator ─────────────────────────────────

const generateArticleSchema = (post, entity, site, assets, baseUrl) => {
  const schema = {
    '@type': 'Article',
    headline: removeEmojis(post.name),
    description: extractDescription(post.description) || '',
    image: `${baseUrl}/${post.imageUrl}`,
    url: `${baseUrl}/${entity.routes.detailPath || ''}/${post.slug}/`,
    author: {
      '@type': 'Person',
      name: site.authorName || site.name,
    },
    datePublished: post.date || new Date().toISOString(),
    dateModified: post.date || new Date().toISOString(),
    publisher: {
      '@type': 'Organization',
      name: site.name,
      logo: {
        '@type': 'ImageObject',
        url: assets.logo,
      },
    },
    keywords: post.tags ? post.tags.join(', ') : '',
    articleSection: post.tags?.length ? post.tags[0] : '',
    isAccessibleForFree: true,
  };

  if (post.instagramUrl) {
    const embedUrl = post.instagramUrl.endsWith('/')
      ? `${post.instagramUrl}embed/`
      : `${post.instagramUrl}/embed/`;
    schema.video = {
      '@type': 'VideoObject',
      name: removeEmojis(post.name),
      description: post.description.split('\n').filter(l => l.trim().length > 0)[1] || '',
      contentUrl: post.instagramUrl,
      embedUrl,
      uploadDate: post.date || new Date().toISOString(),
      thumbnailUrl: `${baseUrl}/${post.imageUrl}`,
      url: post.instagramUrl,
    };
  }

  return schema;
};

// ─── Registry ──────────────────────────────────────────

export const SCHEMA_GENERATORS = {
  Recipe: generateRecipeSchema,
  Article: generateArticleSchema,
};

export const DEFAULT_SCHEMA_TYPE = 'Article';

/**
 * Generate the post schema for the given type.
 * Falls back to Article if the type is not registered.
 */
export function generateSchema(type, post, entity, site, assets, baseUrl) {
  const generator = SCHEMA_GENERATORS[type] || SCHEMA_GENERATORS[DEFAULT_SCHEMA_TYPE];
  return generator(post, entity, site, assets, baseUrl);
}
